import React, { useLayoutEffect, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { ref, update, serverTimestamp } from 'firebase/database';
import { updateProfile } from 'firebase/auth';
import { useTranslation } from 'react-i18next';
import { database } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';
import AppearanceSwitch from './profile/components/AppearanceSwitch';
import countries from '../utils/countries';
import { translateCountry } from '../utils/translationUtils';
import { safeAuthDestination } from '../utils/authFlow';
import AuthLayout, { AuthNotice, SetupSteps } from './Auth/AuthLayout';
import AccountLoadError from './Auth/AccountLoadError';
import AppLoading from './AppLoading';

export function CompanySetup({ profile }) {
  const { t } = useTranslation('auth');
  const { language } = useLanguage();
  const { currentUser, refreshAccount, logout } = useAuth();
  const { setThemePreference } = useTheme();
  const appearanceKey = `onboarding-appearance:${currentUser?.uid}`;
  useLayoutEffect(() => {
    let appearance = 'dark';
    try { if (sessionStorage.getItem(appearanceKey) === 'light') appearance = 'light'; } catch (_) { /* Storage is optional. */ }
    setThemePreference(appearance);
  }, [appearanceKey, setThemePreference]);
  const saveAppearance = appearance => {
    try { sessionStorage.setItem(appearanceKey, appearance); } catch (_) { /* The theme still applies to this session. */ }
  };
  const navigate = useNavigate();
  const [step, setStep] = useState(profile.accountSetup?.step === 'profile' && profile.companyName?.trim() && profile.country?.trim() && profile.city?.trim() ? 2 : 1);
  const [fields, setFields] = useState({ companyName: profile.companyName || '', city: profile.city || '', country: profile.country || '', description: profile.description || '',
    firstName: profile.ownerFirstName || '', lastName: profile.ownerLastName || '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const headingRef = useRef(null);
  const change = event => setFields(previous => ({ ...previous, [event.target.name]: event.target.value }));
  const save = async (event, skip = false) => {
    event?.preventDefault();
    if (busy) return;
    setError('');
    if (!fields.companyName.trim() || !fields.city.trim() || !fields.country) { setError('setup.required'); return; }
    setBusy(true);
    try {
      const values = {
        companyName: fields.companyName.trim(), city: fields.city.trim(), country: fields.country,
        ownerFirstName: fields.firstName.trim(), ownerLastName: fields.lastName.trim(),
        ownerName: [fields.firstName.trim(), fields.lastName.trim()].filter(Boolean).join(' '),
        uid: currentUser.uid, 'settings/language': language,
        'accountSetup/version': 1, 'accountSetup/status': step === 2 ? 'complete' : 'incomplete',
        'accountSetup/step': step === 2 ? 'complete' : 'profile',
      };
      if (!profile.accountSetup) {
        values.linkedin = 'none'; values.filterSubstageEnabled = true;
      }
      if (step === 2) {
        values.description = skip ? '' : fields.description.trim();
        values['accountSetup/completedAt'] = serverTimestamp();
      }
      if (values.ownerName && values.ownerName !== currentUser.displayName) {
        await updateProfile(currentUser, { displayName: values.ownerName });
      }
      await update(ref(database, `companies/${currentUser.uid}`), values);
      if (step === 1) {
        setStep(2);
        requestAnimationFrame(() => headingRef.current?.focus());
      } else {
        const saved = await refreshAccount();
        if (saved?.complete) navigate('/', { replace: true });
      }
    } catch (_) { setError('setup.saveError'); }
    finally { setBusy(false); }
  };
  const sortedCountries = [...countries].sort((a, b) => translateCountry(a, language).localeCompare(translateCountry(b, language), language));
  return <AuthLayout centered onboarding mode="signup" title={t(step === 1 ? 'setup.companyTitle' : 'setup.profileTitle')}
    subtitle={t(step === 1 ? 'setup.companySubtitle' : 'setup.profileSubtitle')} steps={<SetupSteps current={step - 1} />}
    footer={<button className="auth-text-button" type="button" onClick={logout} disabled={busy}>{t('shared.signOut')}</button>}>
    <AuthNotice>{error && t(error)}</AuthNotice>
    <form aria-labelledby="auth-title" className="auth-form" onSubmit={save} aria-busy={busy}>
      {step === 1 ? <>
        <div className="auth-appearance">
          <p>{t('setup.darkDefault')}</p>
          <AppearanceSwitch onChange={saveAppearance} />
        </div>
        <div className="auth-row">
          <div className="auth-field"><label htmlFor="firstName">{t('setup.firstName')} <span className="auth-hint">({t('shared.optional')})</span></label>
            <input id="firstName" name="firstName" autoComplete="given-name" value={fields.firstName} onChange={change} maxLength={80} disabled={busy} />
          </div>
          <div className="auth-field"><label htmlFor="lastName">{t('setup.lastName')} <span className="auth-hint">({t('shared.optional')})</span></label>
            <input id="lastName" name="lastName" autoComplete="family-name" value={fields.lastName} onChange={change} maxLength={80} disabled={busy} />
          </div>
        </div>
        <div className="auth-field"><label htmlFor="companyName">{t('signup.companyName')}</label>
          <input id="companyName" name="companyName" autoComplete="organization" value={fields.companyName} onChange={change} required maxLength={160} disabled={busy} />
        </div>
        <div className="auth-row">
          <div className="auth-field"><label htmlFor="country">{t('signup.country')}</label>
            <select id="country" name="country" autoComplete="country-name" value={fields.country} onChange={change} required disabled={busy}>
              <option value="" disabled>{t('signup.selectCountry')}</option>
              {sortedCountries.map(country => <option key={country} value={country}>{translateCountry(country, language)}</option>)}
            </select>
          </div>
          <div className="auth-field"><label htmlFor="city">{t('signup.city')}</label>
            <input id="city" name="city" autoComplete="address-level2" value={fields.city} onChange={change} required maxLength={120} disabled={busy} />
          </div>
        </div>
        <button className="auth-primary" type="submit" disabled={busy}>{t(busy ? 'setup.saving' : 'setup.continue')}</button>
        <p className="auth-hint">{t('setup.editLater')}</p>
      </> : <>
        <div className="auth-field"><label htmlFor="description">{t('signup.description')} <span className="auth-hint">({t('shared.optional')})</span></label>
          <textarea ref={headingRef} id="description" name="description" rows={5} maxLength={2000} value={fields.description} onChange={change}
            placeholder={t('setup.descriptionPlaceholder')} aria-describedby="description-hint" disabled={busy} />
          <p id="description-hint" className="auth-hint">{t('setup.descriptionHint')}</p>
        </div>
        <div className="auth-actions">
          <button className="auth-secondary" type="button" disabled={busy} onClick={() => { setStep(1); setError(''); }}>{t('shared.back')}</button>
          <button className="auth-primary" type="submit" disabled={busy}>{t(busy ? 'setup.saving' : 'setup.finish')}</button>
        </div>
        <button className="auth-text-button" type="button" disabled={busy} onClick={() => save(null, true)}>{t('setup.skip')}</button>
      </>}
    </form>
  </AuthLayout>;
}

export default function EmployerOnboarding() {
  const { currentUser, emailVerified, accountLoading, accountError, accountProfile } = useAuth();
  const location = useLocation();
  if (!currentUser) return <Navigate to="/login" replace state={{ from: location }} />;
  if (!emailVerified) return <Navigate to="/verify-email" replace state={location.state} />;
  if (accountLoading) return <AppLoading />;
  if (accountError) return <AccountLoadError />;
  if (accountProfile?.complete) return <Navigate to={safeAuthDestination(location.state?.from)} replace />;
  if (!accountProfile) return <AppLoading />;
  return <CompanySetup key={currentUser.uid} profile={accountProfile} />;
}
